import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, getActiveTenant, setActiveTenant } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState, PageHeader, Spinner } from "@/components/shared";
import { cn } from "@/lib/utils";

const TIMEZONES = ["Europe/Madrid", "Europe/London", "Atlantic/Canary", "Europe/Lisbon", "Europe/Paris", "UTC"];
const CURRENCIES = ["EUR", "GBP", "USD"];

/** Fleet administration for super admins: create, rename, retime and delete whole fleets.
 *  Each one is fully isolated — its own vehicles, drivers, trips, settings and users. */
export default function Tenants() {
  const { user, can } = useAuth();
  const t = useT();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<any>({ name: "", timezone: "Europe/Madrid", currency: "EUR", adminEmail: "", adminName: "", adminPassword: "" });
  const [editing, setEditing] = useState<string | null>(null);
  const [edit, setEdit] = useState<any>({});
  // Daily AI allowance lives behind its own endpoint, so it keeps its own draft value.
  // "" means "no override" — the fleet falls back to the platform default.
  const [aiLimit, setAiLimit] = useState<string>("");
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["tenants"],
    queryFn: async () => (await api.get("/tenants")).data,
    enabled: can("*"),
  });

  const create = useMutation({
    mutationFn: () => api.post("/tenants", {
      name: form.name, timezone: form.timezone, currency: form.currency,
      ...(form.adminEmail && form.adminPassword
        ? { adminEmail: form.adminEmail, adminName: form.adminName || form.name, adminPassword: form.adminPassword }
        : {}),
    }),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ["tenants"] });
      setCreating(false);
      setForm({ name: "", timezone: "Europe/Madrid", currency: "EUR", adminEmail: "", adminName: "", adminPassword: "" });
      toast.success(r.data.admin ? `Fleet created · login ${r.data.admin.email}` : "Fleet created");
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const save = useMutation({
    mutationFn: async (row: any) => {
      if (Object.keys(edit).length) await api.patch(`/tenants/${row.id}`, edit);
      const before = row.aiDailyLimit ?? null;
      const after = aiLimit.trim() === "" ? null : Number(aiLimit);
      if (after !== before) await api.patch(`/tenants/${row.id}/ai-limit`, { dailyLimit: after });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tenants"] }); setEditing(null); toast.success("Fleet updated"); },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: ({ id, name }: any) => api.delete(`/tenants/${id}`, { params: { confirmName: name } }),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ["tenants"] });
      setConfirmDelete(null); setConfirmText("");
      const n = Object.values(r.data.removed || {}).reduce((a: any, b: any) => a + b, 0);
      toast.success(`${r.data.name} deleted · ${n} records removed`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (!can("*")) return <EmptyState text="Only a super admin can manage fleets." />;

  const active = getActiveTenant() || (user as any)?.tenantId;
  const list: any[] = data || [];

  return (
    <div data-testid="tenants-page">
      <PageHeader
        title={t("Fleets")}
        subtitle={`${list.length} fleets · each one fully isolated: its own vehicles, drivers, trips and settings · AI / day is today's usage against that fleet's included allowance`}
        actions={<Button data-testid="new-tenant-button" onClick={() => setCreating((v) => !v)} className="bg-blue-600 hover:bg-blue-500 text-white">
          <Plus className="h-4 w-4 mr-1" />{t("New fleet")}
        </Button>} />

      {creating && (
        <div className="tg-card p-5 mb-4 space-y-3" data-testid="new-tenant-form">
          <div className="tg-label">{t("New fleet")}</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block text-xs text-slate-400">Fleet name
              <Input data-testid="new-tenant-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="Radio Taxi Ibiza" className="mt-1 bg-[#0f141f] border-[#26334d] text-sm" /></label>
            <label className="block text-xs text-slate-400">Timezone
              <select value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                      data-testid="new-tenant-tz"
                      className="mt-1 w-full h-9 rounded-md bg-[#0f141f] border border-[#26334d] text-sm px-2 text-slate-100">
                {TIMEZONES.map((z) => <option key={z} value={z}>{z}</option>)}
              </select></label>
            <label className="block text-xs text-slate-400">Currency
              <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}
                      data-testid="new-tenant-currency"
                      className="mt-1 w-full h-9 rounded-md bg-[#0f141f] border border-[#26334d] text-sm px-2 text-slate-100">
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select></label>
          </div>
          <div className="text-[11px] text-slate-500 pt-1">Optional: create the fleet manager now so the customer can log in straight away.</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input data-testid="new-tenant-admin-email" value={form.adminEmail} onChange={(e) => setForm({ ...form, adminEmail: e.target.value })}
                   placeholder="manager@fleet.com" className="bg-[#0f141f] border-[#26334d] text-sm" />
            <Input data-testid="new-tenant-admin-name" value={form.adminName} onChange={(e) => setForm({ ...form, adminName: e.target.value })}
                   placeholder="Manager name" className="bg-[#0f141f] border-[#26334d] text-sm" />
            <Input data-testid="new-tenant-admin-password" type="password" value={form.adminPassword}
                   onChange={(e) => setForm({ ...form, adminPassword: e.target.value })}
                   placeholder="Password (min 6)" className="bg-[#0f141f] border-[#26334d] text-sm" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" data-testid="create-tenant-button" disabled={!form.name || create.isPending}
                    onClick={() => create.mutate()} className="bg-blue-600 hover:bg-blue-500 text-white">
              {create.isPending ? "Creating…" : "Create fleet"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setCreating(false)} className="text-slate-400">{t("Cancel")}</Button>
          </div>
        </div>
      )}

      {isLoading ? <Spinner /> : !list.length ? <EmptyState text="No fleets yet" /> : (
        <div className="tg-card overflow-hidden">
          <Table data-testid="tenants-table">
            <TableHeader><TableRow className="border-[#26334d] hover:bg-transparent">
              {["Fleet", "Timezone", "Currency", "AI / day", "Vehicles", "Drivers", "Users", "Journeys", "Trips", ""].map((h) => (
                <TableHead key={h} className="tg-label">{h}</TableHead>
              ))}
            </TableRow></TableHeader>
            <TableBody>
              {list.map((x: any) => (
                <TableRow key={x.id} data-testid={`tenant-row-${x.id}`}
                          className={cn("border-[#26334d] hover:bg-[#1f2a3e]", x.id === active && "bg-blue-500/5")}>
                  <TableCell>
                    {editing === x.id ? (
                      <Input value={edit.name ?? x.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                             data-testid={`tenant-name-input-${x.id}`} className="h-7 bg-[#0f141f] border-[#26334d] text-sm" />
                    ) : (
                      <div className="flex items-center gap-2">
                        <Building2 className="h-3.5 w-3.5 text-slate-500" />
                        <span className="text-sm text-slate-100">{x.name}</span>
                        {x.id === active && <span className="font-mono text-[9px] px-1 rounded bg-blue-500/20 text-blue-300">ACTIVE</span>}
                        {x.isOwn && <span className="font-mono text-[9px] px-1 rounded bg-slate-500/20 text-slate-400">MINE</span>}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-400">
                    {editing === x.id ? (
                      <select value={edit.timezone ?? x.timezone} onChange={(e) => setEdit({ ...edit, timezone: e.target.value })}
                              className="h-7 rounded bg-[#0f141f] border border-[#26334d] text-xs px-1 text-slate-100">
                        {TIMEZONES.map((z) => <option key={z} value={z}>{z}</option>)}
                      </select>
                    ) : x.timezone}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-400">
                    {editing === x.id ? (
                      <select value={edit.currency ?? x.settings?.currency ?? "EUR"} onChange={(e) => setEdit({ ...edit, currency: e.target.value })}
                              className="h-7 rounded bg-[#0f141f] border border-[#26334d] text-xs px-1 text-slate-100">
                        {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    ) : (x.settings?.currency || "EUR")}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {editing === x.id ? (
                      <Input value={aiLimit} onChange={(e) => setAiLimit(e.target.value.replace(/[^0-9]/g, ""))}
                             data-testid={`tenant-ai-limit-input-${x.id}`} placeholder="default"
                             title="Queries per day included for this fleet. Blank uses the platform default."
                             className="h-7 w-20 bg-[#0f141f] border-[#26334d] text-xs text-right" />
                    ) : (
                      <span data-testid={`tenant-ai-limit-${x.id}`}
                            className={x.aiDailyLimit != null ? "text-slate-200" : "text-slate-500"}>
                        {`${x.aiUsedToday ?? 0} / ${x.aiDailyLimit ?? t("platform default")}`}
                      </span>
                    )}
                  </TableCell>
                  {["vehicles", "drivers", "users", "journeys", "trips"].map((k) => (
                    <TableCell key={k} className="font-mono text-xs text-slate-300">{x.counts?.[k] ?? 0}</TableCell>
                  ))}
                  <TableCell>
                    <div className="flex items-center gap-1 justify-end">
                      {editing === x.id ? (
                        <>
                          <Button size="icon" variant="ghost" data-testid={`tenant-save-${x.id}`} disabled={save.isPending} onClick={() => save.mutate(x)}
                                  className="h-7 w-7 text-emerald-400"><Check className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => setEditing(null)} className="h-7 w-7 text-slate-500"><X className="h-4 w-4" /></Button>
                        </>
                      ) : (
                        <>
                          {x.id !== active && (
                            <Button size="sm" variant="ghost" data-testid={`tenant-switch-${x.id}`}
                                    onClick={() => { setActiveTenant(x.id); qc.clear(); window.location.reload(); }}
                                    className="h-7 text-[11px] text-blue-300">Open</Button>
                          )}
                          <Button size="icon" variant="ghost" data-testid={`tenant-edit-${x.id}`}
                                  onClick={() => { setEditing(x.id); setEdit({}); setAiLimit(x.aiDailyLimit != null ? String(x.aiDailyLimit) : ""); }}
                                  className="h-7 w-7 text-slate-500 hover:text-slate-200"><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button size="icon" variant="ghost" data-testid={`tenant-delete-${x.id}`}
                                  disabled={x.isOwn}
                                  title={x.isOwn ? "You cannot delete your own fleet" : "Delete fleet and all its data"}
                                  onClick={() => { setConfirmDelete(x.id); setConfirmText(""); }}
                                  className="h-7 w-7 text-slate-500 hover:text-rose-400 disabled:opacity-30"><Trash2 className="h-3.5 w-3.5" /></Button>
                        </>
                      )}
                    </div>
                    {confirmDelete === x.id && (
                      <div className="mt-2 p-2 border border-rose-500/30 rounded-md bg-rose-500/5 space-y-2" data-testid={`tenant-confirm-${x.id}`}>
                        <div className="text-[11px] text-rose-300">
                          This deletes the fleet and every vehicle, driver, journey, trip and user in it. Irreversible.
                          Type <b>{x.name}</b> to confirm.
                        </div>
                        <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                               data-testid={`tenant-confirm-input-${x.id}`}
                               className="h-7 bg-[#0f141f] border-[#26334d] text-xs" />
                        <div className="flex gap-1">
                          <Button size="sm" disabled={confirmText !== x.name || remove.isPending}
                                  data-testid={`tenant-confirm-delete-${x.id}`}
                                  onClick={() => remove.mutate({ id: x.id, name: x.name })}
                                  className="h-7 text-[11px] bg-rose-600 hover:bg-rose-500 text-white">Delete for good</Button>
                          <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(null)} className="h-7 text-[11px] text-slate-400">{t("Cancel")}</Button>
                        </div>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
